import { useEffect, useMemo, useState } from 'react';
import { Image, ChevronRight, X, Loader2, Zap, Check, Search, GalleryHorizontal } from 'lucide-react';

export interface StaticStrategyOption {
  key: string;
  label: string;
  description: string;
  pipeline: string;
  picker: 'none' | 'schemas' | 'carousel';
}

export interface StaticSchemaOption {
  key: string;
  number: string;
  label: string;
  category: string;
  funnel: string;
  description: string;
}

export interface StaticCategoryOption {
  letter: string;
  title: string;
  funnel: string;
}

export interface StaticStrategySelection {
  selectedSchemas?: string[];
  carouselSchema?: string | null;
}

interface StaticStrategyPickerModalProps {
  strategies: StaticStrategyOption[];
  schemas: StaticSchemaOption[];
  categories: StaticCategoryOption[];
  onSelect: (strategy: StaticStrategyOption, selection: StaticStrategySelection) => void;
  onClose: () => void;
}

const FUNNEL_LABELS: Record<string, string> = {
  TOFU: 'TOFU',
  TOFU_MOFU: 'TOFU/MOFU',
  MOFU: 'MOFU',
  MOFU_BOFU: 'MOFU/BOFU',
  BOFU: 'BOFU',
  CAROUSEL: 'Karuzela',
};

function categoryChipLabel(category: StaticCategoryOption): string {
  // Tytuł kategorii z katalogu, bez licznika i em-dashy (konwencja UI)
  return category.title.replace(/\s*\(.*\)\s*$/, '').replace(/—/g, '-');
}

export function StaticStrategyPickerModal({ strategies, schemas, categories, onSelect, onClose }: StaticStrategyPickerModalProps) {
  const [selectedStrategy, setSelectedStrategy] = useState<StaticStrategyOption | null>(null);
  const [schemasStep, setSchemasStep] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isCarouselPicker = selectedStrategy?.picker === 'carousel';

  const visibleSchemas = useMemo(() => {
    const base = isCarouselPicker ? schemas.filter(schema => schema.category === 'F') : schemas;
    const filtered = categoryFilter ? base.filter(schema => schema.category === categoryFilter) : base;
    const query = search.trim().toLowerCase();
    if (!query) return filtered;
    return filtered.filter(schema =>
      schema.label.toLowerCase().includes(query)
      || schema.number.toLowerCase().includes(query)
      || schema.description.toLowerCase().includes(query),
    );
  }, [schemas, isCarouselPicker, categoryFilter, search]);

  const handleStrategyClick = (strategy: StaticStrategyOption) => {
    if (strategy.picker === 'none') {
      onSelect(strategy, {});
      return;
    }
    setSelectedStrategy(strategy);
    setSelectedKeys([]);
    setCategoryFilter(strategy.picker === 'carousel' ? null : null);
    setSearch('');
    setSchemasStep(true);
  };

  const toggleSchema = (key: string) => {
    setSelectedKeys(prev => (prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]));
  };

  const handleCarouselPick = (key: string | null) => {
    if (!selectedStrategy) return;
    onSelect(selectedStrategy, { carouselSchema: key });
  };

  const handleSchemasStart = () => {
    if (!selectedStrategy || selectedKeys.length === 0) return;
    onSelect(selectedStrategy, { selectedSchemas: selectedKeys });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-surface-raised rounded-xl border border-border w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto ${schemasStep ? 'max-w-2xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">
            {schemasStep && selectedStrategy
              ? (isCarouselPicker ? 'Schemat karuzeli' : `Schematy z katalogu (${selectedKeys.length} wybrane)`)
              : 'Wybierz strategię reklamy statycznej'}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!schemasStep && (
          <p className="text-xs text-text-muted mb-3">
            Strategia określa metodologię: rezultatem są koncepcje i prompty grafik (katalog 100 schematów), z których wygenerujesz gotowe kreacje z shortcode'ami.
          </p>
        )}

        {schemasStep && (
          <button
            onClick={() => {
              setSchemasStep(false);
              setSelectedKeys([]);
            }}
            className="text-[11px] text-text-muted hover:text-text-primary transition-colors mb-3"
          >
            ← Wróć do strategii
          </button>
        )}

        {!schemasStep ? (
          strategies.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-8 text-xs text-text-muted">
              <Loader2 className="w-4 h-4 animate-spin" />
              Ładowanie strategii...
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {strategies.map(strategy => (
                <button
                  key={strategy.key}
                  onClick={() => handleStrategyClick(strategy)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg border bg-surface-hover border-border hover:border-accent-primary/30 hover:bg-surface-hover/80 transition-all text-left"
                >
                  {strategy.picker === 'carousel'
                    ? <GalleryHorizontal className="w-5 h-5 shrink-0 mt-0.5 text-accent-info" />
                    : <Image className="w-5 h-5 shrink-0 mt-0.5 text-accent-info" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary">{strategy.label}</div>
                    <div className="text-[11px] text-text-muted mt-0.5 leading-relaxed">{strategy.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-disabled shrink-0 mt-0.5" />
                </button>
              ))}
            </div>
          )
        ) : (
          <>
            {!isCarouselPicker && (
              <div className="mb-3 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Szukaj schematu (nazwa, numer)..."
                    className="w-full pl-8 pr-3 py-2 text-xs bg-surface-hover border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-primary/40"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setCategoryFilter(null)}
                    className={`px-2 py-1 rounded-full text-[10px] border transition-colors ${
                      categoryFilter === null
                        ? 'bg-accent-primary/10 border-accent-primary/40 text-text-primary'
                        : 'bg-surface-hover border-border text-text-muted hover:text-text-primary'
                    }`}
                  >
                    Wszystkie
                  </button>
                  {categories.map(category => (
                    <button
                      key={category.letter}
                      onClick={() => setCategoryFilter(prev => (prev === category.letter ? null : category.letter))}
                      title={categoryChipLabel(category)}
                      className={`px-2 py-1 rounded-full text-[10px] border transition-colors ${
                        categoryFilter === category.letter
                          ? 'bg-accent-primary/10 border-accent-primary/40 text-text-primary'
                          : 'bg-surface-hover border-border text-text-muted hover:text-text-primary'
                      }`}
                    >
                      {FUNNEL_LABELS[category.funnel] || category.funnel}: {categoryChipLabel(category).split(' - ')[1] || categoryChipLabel(category)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2 max-h-[48vh] overflow-y-auto pr-1">
              {isCarouselPicker && (
                <button
                  onClick={() => handleCarouselPick(null)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg border bg-surface-hover border-border hover:border-accent-primary/30 hover:bg-surface-hover/80 transition-all text-left"
                >
                  <Zap className="w-5 h-5 shrink-0 mt-0.5 text-accent-primary" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary">Rekomendacja AI</div>
                    <div className="text-[11px] text-text-muted mt-0.5 leading-relaxed">
                      AI przeanalizuje produkt i avatara, po czym samo dobierze najlepszy schemat karuzeli.
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-disabled shrink-0 mt-0.5" />
                </button>
              )}
              {visibleSchemas.map(schema => {
                const isSelected = selectedKeys.includes(schema.key);
                return (
                  <button
                    key={schema.key}
                    onClick={() => (isCarouselPicker ? handleCarouselPick(schema.key) : toggleSchema(schema.key))}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'bg-accent-primary/5 border-accent-primary/40'
                        : 'bg-surface-hover border-border hover:border-accent-primary/30 hover:bg-surface-hover/80'
                    }`}
                  >
                    {!isCarouselPicker && (
                      <span className={`w-4 h-4 shrink-0 mt-0.5 rounded border flex items-center justify-center ${
                        isSelected ? 'bg-accent-primary border-accent-primary' : 'border-border'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary">{schema.number} {schema.label}</span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] bg-surface-hover border border-border text-text-muted shrink-0">
                          {FUNNEL_LABELS[schema.funnel] || schema.funnel}
                        </span>
                      </div>
                      <div className="text-[11px] text-text-muted mt-0.5 leading-relaxed line-clamp-3">{schema.description}</div>
                    </div>
                  </button>
                );
              })}
              {visibleSchemas.length === 0 && (
                <div className="py-6 text-center text-xs text-text-muted">Brak schematów dla tego filtra.</div>
              )}
            </div>

            {!isCarouselPicker && (
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={handleSchemasStart}
                  disabled={selectedKeys.length === 0}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
                >
                  <Image className="w-3.5 h-3.5" />
                  Generuj ({selectedKeys.length})
                </button>
                {selectedKeys.length > 0 && (
                  <button
                    onClick={() => setSelectedKeys([])}
                    className="text-[11px] text-text-muted hover:text-text-primary transition-colors"
                  >
                    Wyczyść wybór
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
